/**
 * Fan listing spec (Wave 2 fan unified Phase 1).
 *
 * Canonical id: `fan` (new). Four legacy fan ids are aliases for reads/backfill.
 * Required: `fan_mount_type`, `fan_power_source`. Optional: `blade_size_inches`.
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  joinListingTitleParts,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const FAN_MOUNT_TYPES = ["standing", "ceiling", "wall"] as const;
export type FanMountType = (typeof FAN_MOUNT_TYPES)[number];

export const FAN_POWER_SOURCES = ["rechargeable", "non_rechargeable"] as const;
export type FanPowerSource = (typeof FAN_POWER_SOURCES)[number];

export const CANONICAL_FAN_SUBCATEGORY_ID = "fan" as const;

export const LEGACY_FAN_SUBCATEGORY_IDS = [
  "standing-fan",
  "wall-fan",
  "ceiling-fan",
  "rechargeable-fans",
] as const;

export type LegacyFanSubcategoryId =
  (typeof LEGACY_FAN_SUBCATEGORY_IDS)[number];

export const FAN_SUBCATEGORY_IDS = [
  CANONICAL_FAN_SUBCATEGORY_ID,
  ...LEGACY_FAN_SUBCATEGORY_IDS,
] as const;

export type FanSubcategoryId = (typeof FAN_SUBCATEGORY_IDS)[number];

export const FAN_BLADE_SIZE_INCHES_MIN = 6;
export const FAN_BLADE_SIZE_INCHES_MAX = 60;

export type LegacyFanSubcategoryMapping = {
  legacyId: string;
  canonicalId: typeof CANONICAL_FAN_SUBCATEGORY_ID;
  fanMountType: FanMountType;
  fanPowerSource: FanPowerSource;
};

/** Locked Phase 0 alias map (SIO + reference backfill). */
export const LEGACY_FAN_SUBCATEGORY_MAP: readonly LegacyFanSubcategoryMapping[] =
  [
    {
      legacyId: "standing-fan",
      canonicalId: CANONICAL_FAN_SUBCATEGORY_ID,
      fanMountType: "standing",
      fanPowerSource: "non_rechargeable",
    },
    {
      legacyId: "wall-fan",
      canonicalId: CANONICAL_FAN_SUBCATEGORY_ID,
      fanMountType: "wall",
      fanPowerSource: "non_rechargeable",
    },
    {
      legacyId: "ceiling-fan",
      canonicalId: CANONICAL_FAN_SUBCATEGORY_ID,
      fanMountType: "ceiling",
      fanPowerSource: "non_rechargeable",
    },
    {
      legacyId: "rechargeable-fans",
      canonicalId: CANONICAL_FAN_SUBCATEGORY_ID,
      fanMountType: "standing",
      fanPowerSource: "rechargeable",
    },
  ] as const;

const mapByLegacyId = new Map(
  LEGACY_FAN_SUBCATEGORY_MAP.map((row) => [row.legacyId, row]),
);

export type FanSpecFormState = {
  fanMountType: FanMountType | "";
  fanPowerSource: FanPowerSource | "";
  bladeSizeInches: number | "";
};

export function defaultFanSpecFormState(): FanSpecFormState {
  return {
    fanMountType: "",
    fanPowerSource: "",
    bladeSizeInches: "",
  };
}

export function isFanMountType(value: unknown): value is FanMountType {
  return (
    typeof value === "string" &&
    (FAN_MOUNT_TYPES as readonly string[]).includes(value)
  );
}

export function isFanPowerSource(value: unknown): value is FanPowerSource {
  return (
    typeof value === "string" &&
    (FAN_POWER_SOURCES as readonly string[]).includes(value)
  );
}

export function isFanSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is FanSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (FAN_SUBCATEGORY_IDS as readonly string[]).includes(subcategoryId)
  );
}

export function isCanonicalFanSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return subcategoryId === CANONICAL_FAN_SUBCATEGORY_ID;
}

export function getFanSpecSectionLabel(): string {
  return "Fan details";
}

export function resolveCanonicalFanSubcategoryId(
  subcategoryId: string | null | undefined,
): string | null {
  if (!subcategoryId) return null;
  if (subcategoryId === CANONICAL_FAN_SUBCATEGORY_ID) {
    return CANONICAL_FAN_SUBCATEGORY_ID;
  }
  return mapByLegacyId.get(subcategoryId)?.canonicalId ?? null;
}

export function getLegacyFanSubcategoryMapping(
  subcategoryId: string | null | undefined,
): LegacyFanSubcategoryMapping | null {
  if (!subcategoryId) return null;
  return mapByLegacyId.get(subcategoryId) ?? null;
}

export function inferFanAttrsFromLegacySubcategoryId(
  subcategoryId: string | null | undefined,
): { fanMountType: FanMountType; fanPowerSource: FanPowerSource } | null {
  const mapped = getLegacyFanSubcategoryMapping(subcategoryId);
  if (!mapped) return null;
  return {
    fanMountType: mapped.fanMountType,
    fanPowerSource: mapped.fanPowerSource,
  };
}

/** D8 / misroute titles that must not become `fan`. */
export function isExcludedFromFanBackfillTitle(
  title: string | null | undefined,
): boolean {
  const t = String(title ?? "").toLowerCase();
  if (/air\s*cooler|aircooler/.test(t)) return true;
  if (/air\s*fr[yi]er|airfryer|air\s*freyer/.test(t)) return true;
  return false;
}

export function inferFanMountTypeFromTitle(
  title: string | null | undefined,
): FanMountType | null {
  const t = String(title ?? "").toLowerCase();
  const ceilingIdx = t.lastIndexOf("ceiling");
  const wallIdx = t.search(/\bwall\b/);
  if (ceilingIdx < 0 && wallIdx < 0) return null;
  if (ceilingIdx > wallIdx) return "ceiling";
  if (wallIdx >= 0) return "wall";
  return "ceiling";
}

export function inferFanPowerSourceFromTitle(
  title: string | null | undefined,
): FanPowerSource | null {
  const t = String(title ?? "").toLowerCase();
  if (/non[\s-]?rechargeable/.test(t)) return "non_rechargeable";
  if (/recharge|solar/.test(t)) return "rechargeable";
  return null;
}

/**
 * Resolve mount + power from legacy id defaults, then title overrides.
 * Returns null when title is excluded (D8) or id is not a fan alias/canonical.
 */
export function resolveFanAttrsForLegacySubcategory(
  subcategoryId: string | null | undefined,
  title?: string | null,
): { fanMountType: FanMountType; fanPowerSource: FanPowerSource } | null {
  if (isExcludedFromFanBackfillTitle(title)) return null;

  let defaults = inferFanAttrsFromLegacySubcategoryId(subcategoryId);
  if (!defaults && subcategoryId === CANONICAL_FAN_SUBCATEGORY_ID) {
    defaults = {
      fanMountType: "standing",
      fanPowerSource: "non_rechargeable",
    };
  }
  if (!defaults) return null;

  const mountFromTitle = inferFanMountTypeFromTitle(title);
  const powerFromTitle = inferFanPowerSourceFromTitle(title);

  return {
    fanMountType: mountFromTitle ?? defaults.fanMountType,
    fanPowerSource: powerFromTitle ?? defaults.fanPowerSource,
  };
}

export function formatFanMountTitleWord(mount: FanMountType): string {
  switch (mount) {
    case "standing":
      return "Standing";
    case "ceiling":
      return "Ceiling";
    case "wall":
      return "Wall";
  }
}

/** Include “Rechargeable” only; omit non_rechargeable from titles. */
export function formatFanPowerTitleWord(
  power: FanPowerSource,
): string | undefined {
  return power === "rechargeable" ? "Rechargeable" : undefined;
}

/**
 * Locked: blade? → Rechargeable?+mount → brand? → Fan
 */
export function composeFanTitleFromAttributes(
  attrs: ListingSpecAttributes,
  identity?: ListingTitleIdentity | null,
): string {
  const mount = attrs.fan_mount_type;
  const power = attrs.fan_power_source;
  if (!isFanMountType(mount) || !isFanPowerSource(power)) return "";

  const blade = attrs.blade_size_inches;
  const bladePart =
    typeof blade === "number" && Number.isFinite(blade)
      ? `${Math.round(blade)}In`
      : undefined;

  return composeListingTitleFromSlotsWithIdentity(
    {
      capacity: bladePart,
      form: joinListingTitleParts(
        formatFanPowerTitleWord(power),
        formatFanMountTitleWord(mount),
      ),
      product: "Fan",
    },
    identity,
  );
}

export function buildFanAttributesFromForm(
  form: FanSpecFormState,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = {};
  if (isFanMountType(form.fanMountType)) {
    out.fan_mount_type = form.fanMountType;
  }
  if (isFanPowerSource(form.fanPowerSource)) {
    out.fan_power_source = form.fanPowerSource;
  }
  if (
    typeof form.bladeSizeInches === "number" &&
    Number.isFinite(form.bladeSizeInches)
  ) {
    out.blade_size_inches = form.bladeSizeInches;
  }
  return out;
}

export function getFanSpecFormValidationMessage(
  form: FanSpecFormState,
): string | undefined {
  const issues = validateStoredFanAttributes(buildFanAttributesFromForm(form));
  return issues[0]?.message;
}

export type FanFormHydrateOptions = {
  subcategoryId?: string | null;
  title?: string | null;
};

/** Hydrate sell/edit form from stored `items.attributes` (+ legacy alias fallback). */
export function fanFormFromAttributes(
  attributes: unknown,
  options?: FanFormHydrateOptions,
): FanSpecFormState {
  const base = defaultFanSpecFormState();
  const attrs = asListingSpecAttributes(attributes);
  let fanMountType: FanMountType | "" = isFanMountType(attrs.fan_mount_type)
    ? attrs.fan_mount_type
    : base.fanMountType;
  let fanPowerSource: FanPowerSource | "" = isFanPowerSource(
    attrs.fan_power_source,
  )
    ? attrs.fan_power_source
    : base.fanPowerSource;

  const bladeRaw = attrs.blade_size_inches;
  let bladeSizeInches: number | "" = base.bladeSizeInches;
  if (typeof bladeRaw === "number" && Number.isFinite(bladeRaw)) {
    bladeSizeInches = bladeRaw;
  }

  const legacyMap = getLegacyFanSubcategoryMapping(options?.subcategoryId);
  if (
    legacyMap &&
    (!fanMountType || !fanPowerSource) &&
    !isExcludedFromFanBackfillTitle(options?.title)
  ) {
    const resolved = resolveFanAttrsForLegacySubcategory(
      options?.subcategoryId,
      options?.title,
    );
    if (resolved) {
      if (!fanMountType) fanMountType = resolved.fanMountType;
      if (!fanPowerSource) fanPowerSource = resolved.fanPowerSource;
    }
  }

  return {
    fanMountType,
    fanPowerSource,
    bladeSizeInches,
  };
}

export function validateStoredFanAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const attrs = attributes as ListingSpecAttributes;
  const mount = attrs.fan_mount_type;
  const power = attrs.fan_power_source;
  const blade = attrs.blade_size_inches;

  if (mount === undefined || mount === null || mount === "") {
    issues.push({
      key: "fan_mount_type",
      code: "required",
      message: "Choose how the fan is mounted.",
    });
  } else if (!isFanMountType(mount)) {
    issues.push({
      key: "fan_mount_type",
      code: "invalid_enum",
      message: "Mount type must be standing, ceiling, or wall.",
    });
  }

  if (power === undefined || power === null || power === "") {
    issues.push({
      key: "fan_power_source",
      code: "required",
      message: "Choose rechargeable or non-rechargeable.",
    });
  } else if (!isFanPowerSource(power)) {
    issues.push({
      key: "fan_power_source",
      code: "invalid_enum",
      message: "Power source must be rechargeable or non-rechargeable.",
    });
  }

  if (blade !== undefined && blade !== null && blade !== "") {
    if (typeof blade !== "number" || !Number.isFinite(blade)) {
      issues.push({
        key: "blade_size_inches",
        code: "invalid_type",
        message: "Blade size must be a number (inches).",
      });
    } else if (
      blade < FAN_BLADE_SIZE_INCHES_MIN ||
      blade > FAN_BLADE_SIZE_INCHES_MAX
    ) {
      issues.push({
        key: "blade_size_inches",
        code: "out_of_range",
        message: `Blade size must be between ${FAN_BLADE_SIZE_INCHES_MIN} and ${FAN_BLADE_SIZE_INCHES_MAX} inches.`,
      });
    }
  }

  return issues;
}
